import React, { useEffect, useRef, forwardRef, useCallback, useMemo } from "react";
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { VectorMap } from "@react-jvectormap/core";
import { worldMill } from "@react-jvectormap/world";
import jquery from "jquery";
import ROUTES_ENUMS from 'routes/url.enum';
import { REGION_CODE_TO_NAME } from 'constants/regionNames';

if (typeof window !== 'undefined') {
  window.jQuery = jquery;
  window.$ = jquery;
}

const ForwardedVectorMap = forwardRef((props) => (
  <VectorMap {...props} />
));

ForwardedVectorMap.displayName = 'ForwardedVectorMap';

const FILL_HAS_PACKAGES = '#f97316';
const FILL_SELECTED = '#ea580c';
const FILL_SELECTED_EMPTY = '#64748b';
const FILL_DEFAULT = '#52525b';

const buildRegionFillValues = (highlightedRegionCodes, selectedRegionCode) => {
  const values = {};
  highlightedRegionCodes.forEach((code) => {
    values[code] = selectedRegionCode === code ? FILL_SELECTED : FILL_HAS_PACKAGES;
  });
  if (selectedRegionCode && !values[selectedRegionCode]) {
    values[selectedRegionCode] = FILL_SELECTED_EMPTY;
  }
  return values;
};

const Map = ({
  markers = [],
  onClose = undefined,
  onRegionSelect = undefined,
  selectedRegionCode = null,
  highlightedRegionCodes = [],
}) => {
  const navigate = useNavigate();
  const mapRef = useRef(null);

  const markersKey = useMemo(
    () => markers.map((m) => m.packageId).join(',') || 'none',
    [markers]
  );

  const regionSeries = useMemo(
    () => ({
      regions: [
        {
          attribute: 'fill',
          values: buildRegionFillValues(highlightedRegionCodes, selectedRegionCode),
        },
      ],
    }),
    [highlightedRegionCodes, selectedRegionCode]
  );

  const selectedRegions = useMemo(
    () => (selectedRegionCode ? [selectedRegionCode] : []),
    [selectedRegionCode]
  );

  useEffect(() => {
    const map = mapRef.current;
    if (!map?.setSelectedRegions) return;
    map.setSelectedRegions(selectedRegions);
  }, [selectedRegions]);

  useEffect(() => {
    return () => {
      mapRef.current?.tip?.remove();
    };
  }, []);

  const handleMarkerClick = (e, code) => {
    const marker = markers[code];
    if (marker?.packageId) {
      mapRef.current?.tip?.hide();
      mapRef.current?.tip?.remove();
      window.$('.jvectormap-tip').hide().remove();

      if (typeof onClose === 'function') {
        onClose();
      }
      setTimeout(() => {
        navigate(ROUTES_ENUMS.TOUR_DETAILS.replace(':packageId', marker.packageId));
      }, 300);
    }
  };

  const handleRegionClick = useCallback((e, code) => {
    if (typeof onRegionSelect === 'function') {
      onRegionSelect(code, REGION_CODE_TO_NAME[code] || code);
    }
  }, [onRegionSelect]);

  const handleMarkerTipShow = useCallback((e, el, code) => {
    const marker = markers[code];
    if (!marker) return;
    el.html(marker.name);
    jquery(el).css({
      backgroundColor: 'rgba(15, 15, 20, 0.96)',
      border: '1px solid rgba(249, 115, 22, 0.5)',
      borderRadius: '8px',
      boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
      color: '#fff',
      fontSize: '12px',
      padding: '0',
      zIndex: 10000,
      maxWidth: '280px',
      pointerEvents: 'none',
    });
  }, [markers]);

  const selectedRegionName = selectedRegionCode
    ? REGION_CODE_TO_NAME[selectedRegionCode] || selectedRegionCode
    : null;

  return (
    <div className="w-full rounded-lg relative h-[190px] md:h-[500px]">
      {highlightedRegionCodes.length > 0 && !selectedRegionCode && (
        <div className="absolute top-2 left-2 z-20 rounded-lg bg-black/85 px-3 py-2 text-xs text-white md:text-sm border border-orange-500/40">
          {highlightedRegionCodes.length}{' '}
          {highlightedRegionCodes.length === 1 ? 'country' : 'countries'} highlighted — hover pins for details
        </div>
      )}
      {selectedRegionName && (
        <div className="absolute top-2 left-2 z-20 rounded-lg bg-black/85 px-3 py-2 text-xs text-white md:text-sm border border-orange-500">
          {markers.length > 0
            ? `${markers.length} package${markers.length === 1 ? '' : 's'} in ${selectedRegionName} — hover pins for details`
            : `No packages in ${selectedRegionName}. Click again to clear.`}
        </div>
      )}
      {markers.length > 0 && !highlightedRegionCodes.length && !selectedRegionName && (
        <div className="absolute top-2 left-2 z-20 rounded-lg bg-black/85 px-3 py-2 text-xs text-white md:text-sm">
          Hover a pin to see package details
        </div>
      )}
      <ForwardedVectorMap
        key={markersKey}
        map={worldMill}
        backgroundColor="transparent"
        zoomOnScroll={false}
        zoomButtons={false}
        zoomMax={1}
        zoomMin={1}
        regionsSelectable
        selectedRegions={selectedRegions}
        series={regionSeries}
        containerStyle={{
          width: "100%",
          height: "100%",
        }}
        regionStyle={{
          initial: {
            fill: FILL_DEFAULT,
            fillOpacity: 0.45,
            stroke: '#a1a1aa',
            strokeWidth: 0.6,
            strokeOpacity: 0.8,
          },
          hover: {
            fill: '#fdba74',
            fillOpacity: 0.85,
            cursor: 'pointer',
            stroke: '#fff',
            strokeWidth: 1,
          },
          selected: {
            fill: FILL_SELECTED,
            fillOpacity: 0.95,
            stroke: '#fff',
            strokeWidth: 1.5,
          },
          selectedHover: {
            fill: '#fb923c',
            fillOpacity: 1,
            stroke: '#fff',
            strokeWidth: 1.5,
          },
        }}
        markerStyle={{
          initial: {
            fill: '#ef4444',
            stroke: '#ffffff',
            'stroke-width': 2.5,
            r: 11,
          },
          hover: {
            fill: '#f97316',
            stroke: '#ffffff',
            'stroke-width': 3,
            cursor: 'pointer',
            r: 14,
          },
          selected: {
            fill: '#ea580c',
            stroke: '#fff',
            'stroke-width': 3,
            r: 13,
          },
        }}
        markers={markers}
        onMarkerClick={handleMarkerClick}
        onRegionClick={handleRegionClick}
        onMarkerTipShow={handleMarkerTipShow}
        ref={(map) => {
          mapRef.current?.tip?.remove();
          if (map?.mapObject) {
            mapRef.current = map.mapObject;
          } else if (map?.$mapObject) {
            mapRef.current = map.$mapObject;
          } else {
            mapRef.current = null;
          }
          if (mapRef.current?.setSelectedRegions && selectedRegions.length) {
            mapRef.current.setSelectedRegions(selectedRegions);
          }
        }}
        onRegionTipShow={(e, el, code) => {
          const name = REGION_CODE_TO_NAME[code] || code;
          el.html(name);
        }}
      />
    </div>
  );
};

Map.propTypes = {
  markers: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    latLng: PropTypes.arrayOf(PropTypes.number).isRequired,
    packageId: PropTypes.string,
  })),
  onClose: PropTypes.func,
  onRegionSelect: PropTypes.func,
  selectedRegionCode: PropTypes.string,
  highlightedRegionCodes: PropTypes.arrayOf(PropTypes.string),
};

export default Map;
